// src/backend/saveRecruitmentDataClient.js
import { writeBatch, doc, Timestamp } from "firebase/firestore";
import { db } from "./../../backend/firebaseConfig";

const stripUndefined = (v) => {
  if (Array.isArray(v)) return v.map(stripUndefined);
  if (
    v &&
    typeof v === "object" &&
    Object.prototype.toString.call(v) === "[object Object]"
  ) {
    const out = {};
    for (const [k, val] of Object.entries(v)) {
      if (val !== undefined) out[k] = stripUndefined(val);
    }
    return out;
  }
  return v;
};

// Convert cached {seconds,nanoseconds} or Date/ISO → Firestore Timestamp
const toTs = (v) => {
  if (!v) return v;
  if (v instanceof Date) return Timestamp.fromDate(v);
  if (typeof v?.toDate === "function") return Timestamp.fromDate(v.toDate());
  if (typeof v === "string") {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return Timestamp.fromDate(d);
    return v;
  }
  if (typeof v === "object" && "seconds" in v) {
    const ms = v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
    return Timestamp.fromMillis(ms);
  }
  return v;
};

// Deeply convert any timestamp-like values
const reviveTimestampsDeep = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(reviveTimestampsDeep);
  const out = {};
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (
      v &&
      typeof v === "object" &&
      ("seconds" in v ||
        "nanoseconds" in v ||
        v instanceof Date ||
        typeof v?.toDate === "function")
    ) {
      out[k] = toTs(v);
    } else {
      out[k] = reviveTimestampsDeep(v);
    }
  }
  return out;
};

export async function saveAdviserData(docId, data) {
  if (!docId) throw new Error("docId is required");
  data = stripUndefined(data);

  // console.log(`[saveAdviserData] called for docId=${docId}`);
  if (!data) {
    // console.warn("[saveAdviserData] no data provided, aborting.");
    return;
  }

  const batch = writeBatch(db);

  // parent doc
  if (data.baseData) {
    // console.log("[saveAdviserData] writing baseData:", data.baseData);
    batch.set(
      doc(db, "Recruitment", docId),
      reviveTimestampsDeep(data.baseData),
      { merge: true }
    );
  }

  // helper
  const setApp = (name, payload) => {
    if (!payload) return;
    // console.log(
    //   `[saveAdviserData] writing Application Details/${name}:`,
    //   payload
    // );
    batch.set(
      doc(db, "Recruitment", docId, "Application Details", name),
      reviveTimestampsDeep(payload),
      { merge: true }
    );
  };

  // sections
  if (data.personalDetails) {
    // console.log(
    //   "[saveAdviserData] writing Personal Details/Data:",
    //   data.personalDetails
    // );
    batch.set(
      doc(db, "Recruitment", docId, "Personal Details", "Data"),
      reviveTimestampsDeep(data.personalDetails),
      { merge: true }
    );
  }

  if (data.ARDetails) {
    // console.log("[saveAdviserData] writing AR Details/Data:", data.ARDetails);
    batch.set(
      doc(db, "Recruitment", docId, "AR Details", "Data"),
      reviveTimestampsDeep(data.ARDetails),
      { merge: true }
    );
  }

  setApp("Data", data.applicationDetails);
  setApp("Previous Addresses", data.previousAddress);
  setApp("Next of Kin Details", data.nextOfKinDetails);
  setApp("Professional Qualifications", data.professionalQualifications);
  setApp("Life Assurance Companies", data.lifeAssuranceCompanies);
  setApp("Work History", data.workHistory);
  setApp("Company Directorships", data.companyDirectorships);
  setApp("Accountant Details", data.accountantDetails);
  setApp("Business Conduct", data.businessConduct);
  setApp("Fitness and Propriety", data.fitnessAndPropriety);
  setApp("Overall Financial Position", data.overallFinancialPosition);
  setApp("Additional Information", data.additionalInformation);
  setApp("Unemployment Details", data.unemploymentDetails);

  if (data.agencyCode) {
    // console.log(
    //   "[saveAdviserData] writing Agency Codes/Data:",
    //   data.agencyCode
    // );
    batch.set(
      doc(db, "Recruitment", docId, "Agency Codes", "Data"),
      reviveTimestampsDeep(data.agencyCode),
      { merge: true }
    );
  }

  if (data.ARDetails) {
    // console.log("[saveAdviserData] writing AR Details/Data:", data.ARDetails);
    batch.set(
      doc(db, "Recruitment", docId, "AR Details", "Data"),
      reviveTimestampsDeep(data.ARDetails),
      { merge: true }
    );
  }

  try {
    await batch.commit();
    // console.log(`[saveAdviserData] batch commit successful for ${docId}`);
  } catch (err) {
    // console.error(`[saveAdviserData] batch commit failed for ${docId}`, err);
    throw err;
  }
}
